import React, { useRef } from 'react';
import {
  MenuItem,
  Select,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  FormControl,
  InputLabel,
  TextareaAutosize,
  Button,
  Box,
  ListItemText,
  ListItemIcon,
  SelectChangeEvent
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useTheme } from '@mui/system';
import { useGetCategories } from '@hooks/useGetCategories';

import { useGetPopularBrands } from '@hooks/useGetPopularBrands';

import { useGetUserData } from '@hooks/useGetUserData';
import {
  getGetPageDeals,
  useUpdatePageDeal,
  UpdatePageDealRequestData
} from '@hooks/useGetPageDeal';
import { useGetDealLists } from '@hooks/useDealLists';
import { useGetAIDescription, useGetAIInternalLinkText } from '@hooks/useAI';
import { useSnackbar } from '@hooks/useSnackbar';
import { stopPropagationForTab, getPageDealUrlFromSlug } from '@utils/index';
import { sanitizeSlug } from '@components/AdminSlugActions/utils';
import AvailableNewPageDeals from '@components/AdminSlugActions/components/EditPageDeal/components/AvailableNewPageDeals';
import {
  getPageDealOptionsTypesToRender,
  getDealListOptionsToRender,
  getPopularBrandsToRender,
  getCategoryOptionsToRender
} from '@components/AdminSlugActions/components/EditPageDeal/utils';
import { PageDealType } from '@types';
import { usePageDealState } from '@components/AdminSlugActions/components/EditPageDeal/usePageDealState';
import BrandDeals from '@components/DealSummary/components/BrandDeals';
import SpinningIcon from '@components/SpinningIcon';
import RequestReindexingButton from '@components/RequestReindexingButton';
import AIPostTitle from './components/AIPostTitle';
import EditDeals from './components/EditDeals';
import DealListMappings from './components/DealListMappings';
import {
  renderSelect,
  renderCheckbox,
  renderTextEdit,
  renderPostDate,
  renderSelectActiveTimeFrame,
  ItemType,
  isSaveDisabled
} from './helpers';
import EditKeywords from './components/EditKeywords';
import PageDealContent, {
  PageDealContentEditorRef
} from './components/PageDealContent';

const EditPageDeal = ({
  pageDeal,
  onCloseCallback = () => {},
  showJustAvailableDeals = false
}: {
  pageDeal: PageDealType | null;
  onCloseCallback?: () => void;
  showJustAvailableDeals?: boolean;
}) => {
  const editorRef = useRef<PageDealContentEditorRef>(null);

  const { showMessage } = useSnackbar();
  const { data: pageDeals } = getGetPageDeals();
  const { data: popularBrands } = useGetPopularBrands();
  const { data: user } = useGetUserData();
  const { data: dealLists } = useGetDealLists();
  const { mutate: updatePageDeal, isLoading: isUpdatingPageDeal } =
    useUpdatePageDeal();
  const { data: categories } = useGetCategories();
  const { mutate: getAIDescription, isLoading: isLoadingAIDescription } =
    useGetAIDescription();
  const {
    mutate: getAIInternalLinkText,
    isLoading: isLoadingAIInternalLinkText
  } = useGetAIInternalLinkText();
  const theme = useTheme();

  const state = usePageDealState(pageDeal);
  const {
    dealListId,
    setDealListId,
    menuTitle,
    setMenuTitle,
    imageLarge,
    setImageLarge,
    imageSmall,
    setImageSmall,
    selectedPageDeal,
    type,
    setType,
    ASINsToRemove,
    setASINsToRemove,
    tag,
    setTag,
    slug,
    setSlug,
    title,
    setTitle,
    description,
    setDescription,
    showSort,
    setShowSort,
    isActive,
    setIsActive,
    mailingListSignUpUrl,
    setMailingListSignUpUrl,
    showCategoryFilter,
    showBrandFilter,
    setShowBrandFilter,
    showCouponFilter,
    setShowCouponFilter,
    strategyId,
    setStrategyId,
    setShowCategoryFilter,
    activeTimeFrame,
    setActiveTimeFrame,
    showSecondaryCategoryFilter,
    setShowSecondaryCategoryFilter,
    showSearch,
    setShowSearch,
    internalLinkText,
    setInternalLinkText,
    brand,
    setBrand,
    category,
    setCategory,
    keyword,
    setKeyword,
    openModal,
    setOpenModal,
    postDate,
    setPostDate,
    setEmptyData,
    handleOpenModal,
    getUpdateData
  } = state;

  if (!user?.isAdmin) {
    return null;
  }

  const getUpdateObject = (): UpdatePageDealRequestData => {
    return {
      data: getUpdateData(),
      dealsToRemoveFromDealList: ASINsToRemove
    };
  };

  const handleOuterSave = () => {
    if (editorRef.current) {
      editorRef.current.save();
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    onCloseCallback();
    setEmptyData();
  };

  const handleAddOrUpdate = async () => {
    handleOuterSave();
    if (selectedPageDeal?.id) {
      showMessage('Updating deal...');
    } else {
      showMessage('Adding deal...');
    }
    updatePageDeal(getUpdateObject());
    handleCloseModal();
  };

  const handleClickGetAIDescription = async () => {
    await getAIDescription(
      {
        pageDealName: title.replace('{numLiveDeals}', 'Lots of'),
        pageDealDescription: description,
        id: selectedPageDeal?.id || null
      },
      {
        onSuccess: ({ suggestion }) => {
          setDescription(suggestion);
          showMessage('Suggestion applied');
        }
      }
    );
  };

  const handleClickGetAIInternalLinkText = async () => {
    await getAIInternalLinkText(
      {
        pageDealName: title.replace('{numLiveDeals}', 'Lots of'),
        pageDealDescription: description
      },
      {
        onSuccess: ({ suggestion }) => {
          setInternalLinkText(suggestion);
          showMessage('Suggestion applied');
        }
      }
    );
  };

  const handleSelectCategory = (e: SelectChangeEvent<string>) => {
    setCategory(e.target.value);

    if (title === '' || !title) {
      if (type === 'TOP_CHEAP_CATEGORY_DEALS_UNDER_3') {
        setTitle(
          `{numLiveDeals} Deals on Amazon ${e.target.value} to Score for Under $3`
        );
        setMenuTitle(`Cheap Deals on ${e.target.value}`);
      } else {
        setTitle(`Amazon Deals on ${e.target.value}`);
        setMenuTitle(`Deals on ${e.target.value}`);
      }
      setSlug(sanitizeSlug(e.target.value));
    }
  };

  const handleSelectBrand = (e: SelectChangeEvent<string>) => {
    setBrand(e.target.value);
    if (title === '' || !title) {
      setTitle(`Amazon's BEST Deals on ${e.target.value}`);
      setMenuTitle(`Deals on ${e.target.value}`);
      setSlug(sanitizeSlug(e.target.value));
    }
  };

  const renderBrandOptions = () => {
    const popularBrandsToRender = getPopularBrandsToRender(
      !!selectedPageDeal,
      popularBrands,
      pageDeals,
      brand
    );

    return renderSelect(
      'Brand',
      popularBrandsToRender,
      brand,
      handleSelectBrand,
      'Select Brand'
    );
  };

  const renderSelectCategory = () => {
    const categoriesToRender = getCategoryOptionsToRender(
      !!selectedPageDeal,
      categories,
      pageDeals,
      type
    );

    return renderSelect(
      'Category',
      categoriesToRender,
      category,
      handleSelectCategory
    );
  };

  const renderItems = (items: ItemType[]) => {
    return items.map((section) => (
      <MenuItem key={section.value} value={section.value}>
        {section.name}
      </MenuItem>
    ));
  };

  const renderRequestReindexingButton = () => {
    if (!selectedPageDeal?.id || !selectedPageDeal?.slug) {
      return null;
    }

    const url = getPageDealUrlFromSlug(selectedPageDeal.slug);

    return (
      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        marginBottom={1}
      >
        <RequestReindexingButton url={url} />
      </Box>
    );
  };

  const renderSelectDealList = () => {
    const dealListOptionsToRender = getDealListOptionsToRender(
      !!selectedPageDeal,
      dealLists,
      dealListId,
      pageDeals
    );

    return (
      <FormControl margin="normal" fullWidth>
        <InputLabel
          id="select-deal-list"
          sx={{
            backgroundColor: theme.palette.background.paper,
            padding: '0 4px'
          }}
        >
          Type
        </InputLabel>
        <Select
          labelId="select-deal-list"
          value={dealListId}
          onChange={(e) => setDealListId(e.target.value as number)}
          displayEmpty
        >
          <MenuItem value="" disabled>
            Select Deal List
          </MenuItem>
          {renderItems(dealListOptionsToRender)}
        </Select>
      </FormControl>
    );
  };

  const renderSelectDealType = () => {
    const pageDealTypesToRender = getPageDealOptionsTypesToRender(
      !!selectedPageDeal,
      pageDeals
    );

    return renderSelect(
      'Type',
      pageDealTypesToRender,
      state.type,
      (e: SelectChangeEvent<string>) => setType(e.target.value),
      'Select type of list'
    );
  };

  const renderDescription = () => {
    return (
      <>
        <FormControl margin="normal" fullWidth>
          <TextareaAutosize
            minRows={3}
            placeholder="Description"
            value={description || ''}
            onChange={(e) => setDescription(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          />
        </FormControl>
        <Button onClick={handleClickGetAIDescription}>
          {isLoadingAIDescription ? 'Loading...' : 'Get AI Description'}
        </Button>
      </>
    );
  };

  const renderInternalLinkText = () => {
    return (
      <>
        <FormControl margin="normal" fullWidth>
          <TextareaAutosize
            minRows={2}
            placeholder="Internal Link Text (between ** will be linked text)"
            value={internalLinkText || ''}
            onChange={(e) => setInternalLinkText(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          />
        </FormControl>
        <Button onClick={handleClickGetAIInternalLinkText}>
          {isLoadingAIInternalLinkText
            ? 'Loading...'
            : 'Get AI Internal Link Text'}
        </Button>
      </>
    );
  };

  const renderCheckboxOptions = () => {
    return (
      <>
        {renderCheckbox(
          isActive,
          () => setIsActive(!isActive),
          'This list is active'
        )}
        {renderCheckbox(showSort, () => setShowSort(!showSort), 'Show sort')}
        {renderCheckbox(
          showCategoryFilter,
          () => setShowCategoryFilter(!showCategoryFilter),
          'Show category filter'
        )}
        {renderCheckbox(
          showBrandFilter,
          () => setShowBrandFilter(!showBrandFilter),
          'Show brand filter'
        )}

        {renderCheckbox(
          showCouponFilter,
          () => setShowCouponFilter(!showCouponFilter),
          'Show coupon filter'
        )}

        {renderCheckbox(
          showSecondaryCategoryFilter,
          () => setShowSecondaryCategoryFilter(!showSecondaryCategoryFilter),
          'Show secondary category filter'
        )}
        {renderCheckbox(
          showSearch,
          () => setShowSearch(!showSearch),
          'Show search'
        )}
        {renderTextEdit(strategyId, setStrategyId, 'Strategy Id')}
      </>
    );
  };

  const renderTypeOptions = () => {
    switch (type) {
      case 'DEAL_LIST':
        return renderSelectDealList();
      case 'BRAND':
        return renderBrandOptions();
      case 'CATEGORY':
      case 'TOP_CHEAP_CATEGORY_DEALS_UNDER_3':
        return renderSelectCategory();
      case 'TOY_DEAL_KEYWORD':
      case 'TOY_DEAL_CATEGORY_KEYWORD':
        return renderTextEdit(keyword, setKeyword, 'Keyword');
      default:
        return null;
    }
  };

  const renderEditDialogContent = () => {
    return (
      <Box display="flex" flexDirection="column">
        {renderRequestReindexingButton()}
        {renderSelectDealType()}
        {renderTypeOptions()}
        <AIPostTitle value={title || ''} onChange={setTitle} />
        {renderPostDate(postDate, setPostDate)}
        {renderDescription()}
        {renderPageDealContent()}
        {renderEditKeywords()}
        {renderInternalLinkText()}
        {renderTextEdit(slug, setSlug, 'Slug')}
        {renderCheckboxOptions()}
        {renderTextEdit(menuTitle, setMenuTitle, 'Menu Title (short)')}
        {renderTextEdit(tag, setTag, 'Tag')}
        {renderTextEdit(imageLarge, setImageLarge, 'Image large')}
        {renderTextEdit(imageSmall, setImageSmall, 'Image small')}
        {renderTextEdit(
          mailingListSignUpUrl || '',
          setMailingListSignUpUrl,
          'Mailing list Url'
        )}
        {renderSelectActiveTimeFrame(activeTimeFrame, setActiveTimeFrame)}
        {renderEditDealListMappings()}
        {renderEditDeals()}
        {renderBrandDeals()}
      </Box>
    );
  };

  const renderBrandDeals = () => {
    if (!brand || brand === '') {
      return null;
    }

    return <BrandDeals brand={brand} currentASIN="12345" />;
  };

  const renderEditDeals = () => {
    if (!pageDeal?.dealListId && !dealListId) {
      return null;
    }
    return (
      <EditDeals
        dealListId={pageDeal?.dealListId || dealListId}
        onChange={setASINsToRemove}
        ASINsToRemove={ASINsToRemove}
      />
    );
  };

  const renderEditKeywords = () => {
    if (!pageDeal?.id) {
      return null;
    }
    return <EditKeywords pageDealId={pageDeal?.id} />;
  };

  const renderPageDealContent = () => {
    if (!pageDeal?.id) {
      return null;
    }
    return <PageDealContent ref={editorRef} pageDealSlug={pageDeal?.slug} />;
  };

  const renderEditDealListMappings = () => {
    if (!pageDeal?.dealListId && !dealListId) {
      return null;
    }
    return <DealListMappings dealListId={pageDeal?.dealListId || dealListId} />;
  };

  const renderMainButton = () => {
    return pageDeal ? (
      <MenuItem key="admin-edit" onClick={() => handleOpenModal(pageDeal)}>
        <ListItemIcon>
          <EditIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Edit</ListItemText>
      </MenuItem>
    ) : (
      <Button
        variant="contained"
        color="primary"
        onClick={() => handleOpenModal(null)}
        sx={{ height: 'fit-content' }}
      >
        New
      </Button>
    );
  };

  return (
    <>
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        fullWidth
        onKeyDown={stopPropagationForTab}
        maxWidth="lg"
      >
        <DialogTitle>
          {selectedPageDeal ? 'Edit Page Deal' : 'Add Page Deal'}
        </DialogTitle>
        <DialogContent>{renderEditDialogContent()}</DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button
            onClick={handleAddOrUpdate}
            variant="contained"
            color="primary"
            disabled={isSaveDisabled(state) || isUpdatingPageDeal}
            startIcon={
              isUpdatingPageDeal ? <SpinningIcon isLoading /> : undefined
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {showJustAvailableDeals ? (
        <AvailableNewPageDeals handleOpenModal={handleOpenModal} />
      ) : (
        renderMainButton()
      )}
    </>
  );
};

export const AllNewPageDeals = () => {
  const { data: user } = useGetUserData();

  if (!user?.isAdmin) {
    return null;
  }

  return <EditPageDeal pageDeal={null} showJustAvailableDeals />;
};

export const AddPageDeal = () => {
  const { data: user } = useGetUserData();

  if (!user?.isAdmin) {
    return null;
  }

  return <EditPageDeal pageDeal={null} />;
};

export default EditPageDeal;
